import { useRef, useEffect, useState } from 'react';
import maplibregl, { Map } from 'maplibre-gl';
import { cogProtocol } from '@geomatico/maplibre-cog-protocol';
import * as pmtiles from 'pmtiles';
import useGeoData from '../../../store/useGeoData';
// import 'maplibre-gl/dist/maplibre-gl.css';
import "maplibre-theme/icons.default.css";
import "maplibre-theme/classic.css";

// Flag a livello di modulo per tracciare l'aggiunta dei protocolli e il logging dell'inizializzazione
let protocolsAdded = false;
let mapInitializationLogged = false;

// Istanziare pmtilesProtocol a livello di modulo
// Sarà inizializzato una sola volta quando il modulo viene caricato.
const pmtilesProtocol = new pmtiles.Protocol();

// Esportare la funzione per registrare istanze PMTiles
export const registerPmtilesInstance = (httpUrl: string) => {
    if (!pmtilesProtocol) {
        console.error("pmtilesProtocol non è inizializzato. Impossibile registrare l'istanza PMTiles.");
        return;
    }
    try {
        console.log(`Registrazione istanza PMTiles per: ${httpUrl}`);
        const pmtilesInstance = new pmtiles.PMTiles(httpUrl);
        pmtilesProtocol.add(pmtilesInstance);
    } catch (error) {
        console.error(`Errore durante la registrazione dell'istanza PMTiles per ${httpUrl}:`, error);
    }
};

// Definizione di tipo per estendere StyleSpecification con la proprietà 'lights'
type Light = {
    id: string;
    type: 'ambient' | 'directional';
    properties?: {
        color?: string;
        intensity?: number;
        direction?: [number, number];
        castShadows?: boolean;
    };
};

interface StyleSpecificationWithLights extends maplibregl.StyleSpecification {
    lights?: Light[];
}

export const useMapInitialization = (mapContainerRef: React.RefObject<HTMLDivElement | null>) => {
    const mapRef = useRef<Map | null>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    // const [isTerrainSourceReady, setIsTerrainSourceReady] = useState(false); // Rimosso stato terrain
    const updateMapState = useGeoData(state => state.updateMapState);
    const setMapInstance = useGeoData(state => state.setMapInstance); // Importa l'azione

    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return; // Evita reinizializzazioni

        // Recupera lo stato necessario dallo store PRIMA di creare la mappa
        const { activeBasemapId, availableBasemaps } = useGeoData.getState();
        const defaultBasemapId = "openfreemap-liberty"; // Come da specifiche del task e aggiornamenti in basemapSlice.ts

        const initialBasemap = availableBasemaps.find(b => b.id === activeBasemapId);
        let styleUrlToUse = initialBasemap?.styleUrl;

        if (!styleUrlToUse) {
            console.warn(
                `Stile mappa di base attivo con ID "${activeBasemapId}" non trovato o URL di stile mancante. ` +
                `Tentativo di fallback allo stile predefinito: "${defaultBasemapId}".`
            );
            const fallbackBasemap = availableBasemaps.find(b => b.id === defaultBasemapId);
            styleUrlToUse = fallbackBasemap?.styleUrl;

            if (!styleUrlToUse) {
                console.error(
                    `Anche lo stile di mappa di base predefinito "${defaultBasemapId}" non è stato trovato o manca l'URL dello stile. ` +
                    `La mappa potrebbe non inizializzarsi correttamente.`
                );
                // styleUrlToUse rimarrà undefined, MapLibre gestirà l'errore se lo stile non è valido.
            }
        }
        const initialStyleUrl = styleUrlToUse; // Questo sarà undefined se nessuno stile valido è stato trovato
        
        if (!mapInitializationLogged) {
            console.log(`Inizializzazione mappa con stile: ${initialStyleUrl}`);
            mapInitializationLogged = true;
        }

        if (!protocolsAdded) {
            maplibregl.addProtocol('cog', cogProtocol);
            maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile); // Usa l'istanza a livello di modulo
            protocolsAdded = true;
            // console.log("Protocolli MapLibre aggiunti."); // Log opzionale
        }

        const map = new maplibregl.Map({
            container: mapContainerRef.current,
            style: initialStyleUrl, // Usa l'URL dello stile iniziale recuperato dallo store
            center: [12.4964, 41.9028], // Roma come centro iniziale
            zoom: 5,
            maxPitch: 85,
            attributionControl: false, // L'attribuzione base verrà aggiunta separatamente
            renderWorldCopies: false
        });

        map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

        map.on('load', () => {
            setIsMapLoaded(true);

            // --- Configurazione Luci ---
            const currentStyle = map.getStyle() as StyleSpecificationWithLights;
            if (!currentStyle.lights || currentStyle.lights.length === 0) {
                const newStyle: StyleSpecificationWithLights = {
                    ...currentStyle,
                    lights: [
                        {
                            id: 'ambient_light',
                            type: 'ambient',
                            properties: { intensity: 0.5, color: '#ffffff' }
                        },
                        {
                            id: 'directional_light',
                            type: 'directional',
                            properties: { intensity: 0.8, color: '#ffffff', direction: [45, 45], castShadows: false }
                        }
                    ]
                };
                map.setStyle(newStyle as maplibregl.StyleSpecification);
            }

            // --- Event Listener per stato mappa ---
            const handleMapStateChange = () => {
                if (!mapRef.current) return;
                const center = mapRef.current.getCenter();
                updateMapState({
                    latitude: center.lat,
                    longitude: center.lng,
                    zoom: mapRef.current.getZoom(),
                    pitch: mapRef.current.getPitch(),
                    bearing: mapRef.current.getBearing(),
                    bounds: mapRef.current.getBounds(),
                });
            };

            map.on('zoom', handleMapStateChange);
            map.on('pitch', handleMapStateChange);
            map.on('rotate', handleMapStateChange);
            map.on('move', handleMapStateChange); // Cattura anche bounds qui

            // Aggiornamento stato iniziale dopo il caricamento mappa
            handleMapStateChange();

            // Imposta l'istanza della mappa nello store
            setMapInstance(map);
        });

        mapRef.current = map;

        // Cleanup
        return () => {
            if (mapRef.current) {
                mapRef.current.remove(); // Rimuove la mappa e tutti i suoi listener
                mapRef.current = null;
                setMapInstance(null); // Pulisce l'istanza mappa dallo store
            }
            setIsMapLoaded(false);
        };
    }, [mapContainerRef, updateMapState, setMapInstance]); // Aggiunta setMapInstance alle dipendenze

    return { mapRef, isMapLoaded };
};